// src/components/chatbot/markdownLite.jsx
// ponytail: tiny zero-dep markdown for chatbot assistant bubbles.
// Handles **bold**, *italic*, `inline code`, ```block code```, bullet + numbered lists, paragraphs.
// Streamed-partial safe: incomplete markers render as plain text until the close arrives.

import React from 'react';

function renderInline(text, key) {
  const out = [];
  let i = 0;
  let n = 0;
  while (i < text.length) {
    // inline code first so its content isn't interpreted
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > i) {
        out.push(
          <code key={`${key}-${n++}`} className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-[0.85em] font-mono">
            {text.slice(i + 1, end)}
          </code>
        );
        i = end + 1;
        continue;
      }
    }
    // bold **x**
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end > i + 1) {
        out.push(<strong key={`${key}-${n++}`}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    // italic *x* — only when not bordered by word chars (avoids mid-word asterisks)
    if (text[i] === '*') {
      const end = text.indexOf('*', i + 1);
      const before = text[i - 1] || '';
      const after = text[end + 1] || '';
      if (end > i && !/\w/.test(before) && !/\w/.test(after)) {
        out.push(<em key={`${key}-${n++}`}>{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    // plain text run until next special char
    let next = i + 1;
    while (next < text.length && text[next] !== '`' && text[next] !== '*') next++;
    out.push(text.slice(i, next));
    i = next;
  }
  return out;
}

export default function MarkdownLite({ content }) {
  const lines = (content || '').split('\n');
  const blocks = [];
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    const line = lines[i];

    // fenced code block
    if (line.startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      blocks.push(
        <pre key={k++} className="bg-gray-900 text-gray-100 p-2.5 rounded-lg text-xs font-mono overflow-x-auto my-2">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // bullet list
    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={k++} className="list-disc pl-5 my-2 space-y-1">
          {items.map((item, idx) => <li key={idx}>{renderInline(item, `${k}-ul-${idx}`)}</li>)}
        </ul>
      );
      continue;
    }

    // ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={k++} className="list-decimal pl-5 my-2 space-y-1">
          {items.map((item, idx) => <li key={idx}>{renderInline(item, `${k}-ol-${idx}`)}</li>)}
        </ol>
      );
      continue;
    }

    // blank line — skip
    if (line.trim() === '') {
      i++;
      continue;
    }

    // paragraph: consume until blank/list/code fence
    const paraLines = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^[-*]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !lines[i].startsWith('```')
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={k++} className="my-1.5 leading-relaxed">
        {renderInline(paraLines.join(' '), `p${k}`)}
      </p>
    );
  }

  return <div className="markdown-lite">{blocks}</div>;
}
